// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// Code generated by Microsoft (R) AutoRest Code Generator.
// Changes may cause incorrect behavior and will be lost if the code is regenerated.

import type { PagedAsyncIterableIterator, PageSettings } from "@azure/core-paging";
import { setContinuationToken } from "../pagingHelper.js";
import type { GalleryInVMAccessControlProfiles } from "../operationsInterfaces/index.js";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers.js";
import * as Parameters from "../models/parameters.js";
import type { ComputeManagementClient } from "../computeManagementClient.js";
import type { SimplePollerLike, OperationState } from "@azure/core-lro";
import { createHttpPoller } from "@azure/core-lro";
import { createLroSpec } from "../lroImpl.js";
import type {
  GalleryInVMAccessControlProfile,
  GalleryInVMAccessControlProfilesListByGalleryNextOptionalParams,
  GalleryInVMAccessControlProfilesListByGalleryOptionalParams,
  GalleryInVMAccessControlProfilesListByGalleryResponse,
  GalleryInVMAccessControlProfilesGetOptionalParams,
  GalleryInVMAccessControlProfilesGetResponse,
  GalleryInVMAccessControlProfilesCreateOrUpdateOptionalParams,
  GalleryInVMAccessControlProfilesCreateOrUpdateResponse,
  GalleryInVMAccessControlProfileUpdate,
  GalleryInVMAccessControlProfilesUpdateOptionalParams,
  GalleryInVMAccessControlProfilesUpdateResponse,
  GalleryInVMAccessControlProfilesDeleteOptionalParams,
  GalleryInVMAccessControlProfilesDeleteResponse,
  GalleryInVMAccessControlProfilesListByGalleryNextResponse,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Class containing GalleryInVMAccessControlProfiles operations. */
export class GalleryInVMAccessControlProfilesImpl implements GalleryInVMAccessControlProfiles {
  private readonly client: ComputeManagementClient;

  /**
   * Initialize a new instance of the class GalleryInVMAccessControlProfiles class.
   * @param client Reference to the service client
   */
  constructor(client: ComputeManagementClient) {
    this.client = client;
  }

  /**
   * List gallery inVMAccessControlProfiles in a gallery.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param galleryName The name of the Shared Image Gallery.
   * @param options The options parameters.
   */
  public listByGallery(
    resourceGroupName: string,
    galleryName: string,
    options?: GalleryInVMAccessControlProfilesListByGalleryOptionalParams,
  ): PagedAsyncIterableIterator<GalleryInVMAccessControlProfile> {
    const iter = this.listByGalleryPagingAll(resourceGroupName, galleryName, options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listByGalleryPagingPage(resourceGroupName, galleryName, options, settings);
      },
    };
  }

  private async *listByGalleryPagingPage(
    resourceGroupName: string,
    galleryName: string,
    options?: GalleryInVMAccessControlProfilesListByGalleryOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<GalleryInVMAccessControlProfile[]> {
    let result: GalleryInVMAccessControlProfilesListByGalleryResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listByGallery(resourceGroupName, galleryName, options);
      const page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listByGalleryNext(
        resourceGroupName,
        galleryName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      const page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listByGalleryPagingAll(
    resourceGroupName: string,
    galleryName: string,
    options?: GalleryInVMAccessControlProfilesListByGalleryOptionalParams,
  ): AsyncIterableIterator<GalleryInVMAccessControlProfile> {
    for await (const page of this.listByGalleryPagingPage(
      resourceGroupName,
      galleryName,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * List gallery inVMAccessControlProfiles in a gallery.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param galleryName The name of the Shared Image Gallery.
   * @param options The options parameters.
   */
  private _listByGallery(
    resourceGroupName: string,
    galleryName: string,
    options?: GalleryInVMAccessControlProfilesListByGalleryOptionalParams,
  ): Promise<GalleryInVMAccessControlProfilesListByGalleryResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, galleryName, options },
      listByGalleryOperationSpec,
    );
  }

  /**
   * Retrieves information about a gallery inVMAccessControlProfile.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param galleryName The name of the Shared Image Gallery.
   * @param inVMAccessControlProfileName The name of the gallery inVMAccessControlProfile to be
   *                                     retrieved.
   * @param options The options parameters.
   */
  get(
    resourceGroupName: string,
    galleryName: string,
    inVMAccessControlProfileName: string,
    options?: GalleryInVMAccessControlProfilesGetOptionalParams,
  ): Promise<GalleryInVMAccessControlProfilesGetResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, galleryName, inVMAccessControlProfileName, options },
      getOperationSpec,
    );
  }

  /**
   * Create or update a gallery inVMAccessControlProfile.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param galleryName The name of the Shared Image Gallery.
   * @param inVMAccessControlProfileName The name of the gallery inVMAccessControlProfile to be
   *                                     retrieved.
   * @param galleryInVMAccessControlProfile Parameters supplied to the create or update gallery
   *                                        inVMAccessControlProfile operation.
   * @param options The options parameters.
   */
  async beginCreateOrUpdate(
    resourceGroupName: string,
    galleryName: string,
    inVMAccessControlProfileName: string,
    galleryInVMAccessControlProfile: GalleryInVMAccessControlProfile,
    options?: GalleryInVMAccessControlProfilesCreateOrUpdateOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<GalleryInVMAccessControlProfilesCreateOrUpdateResponse>,
      GalleryInVMAccessControlProfilesCreateOrUpdateResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<GalleryInVMAccessControlProfilesCreateOrUpdateResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: {
        resourceGroupName,
        galleryName,
        inVMAccessControlProfileName,
        galleryInVMAccessControlProfile,
        options,
      },
      spec: createOrUpdateOperationSpec,
    });
    const poller = await createHttpPoller<
      GalleryInVMAccessControlProfilesCreateOrUpdateResponse,
      OperationState<GalleryInVMAccessControlProfilesCreateOrUpdateResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Create or update a gallery inVMAccessControlProfile.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param galleryName The name of the Shared Image Gallery.
   * @param inVMAccessControlProfileName The name of the gallery inVMAccessControlProfile to be
   *                                     retrieved.
   * @param galleryInVMAccessControlProfile Parameters supplied to the create or update gallery
   *                                        inVMAccessControlProfile operation.
   * @param options The options parameters.
   */
  async beginCreateOrUpdateAndWait(
    resourceGroupName: string,
    galleryName: string,
    inVMAccessControlProfileName: string,
    galleryInVMAccessControlProfile: GalleryInVMAccessControlProfile,
    options?: GalleryInVMAccessControlProfilesCreateOrUpdateOptionalParams,
  ): Promise<GalleryInVMAccessControlProfilesCreateOrUpdateResponse> {
    const poller = await this.beginCreateOrUpdate(
      resourceGroupName,
      galleryName,
      inVMAccessControlProfileName,
      galleryInVMAccessControlProfile,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * Update a gallery inVMAccessControlProfile.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param galleryName The name of the Shared Image Gallery.
   * @param inVMAccessControlProfileName The name of the gallery inVMAccessControlProfile to be
   *                                     retrieved.
   * @param galleryInVMAccessControlProfile Parameters supplied to the update gallery
   *                                        inVMAccessControlProfile operation.
   * @param options The options parameters.
   */
  async beginUpdate(
    resourceGroupName: string,
    galleryName: string,
    inVMAccessControlProfileName: string,
    galleryInVMAccessControlProfile: GalleryInVMAccessControlProfileUpdate,
    options?: GalleryInVMAccessControlProfilesUpdateOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<GalleryInVMAccessControlProfilesUpdateResponse>,
      GalleryInVMAccessControlProfilesUpdateResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<GalleryInVMAccessControlProfilesUpdateResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: {
        resourceGroupName,
        galleryName,
        inVMAccessControlProfileName,
        galleryInVMAccessControlProfile,
        options,
      },
      spec: updateOperationSpec,
    });
    const poller = await createHttpPoller<
      GalleryInVMAccessControlProfilesUpdateResponse,
      OperationState<GalleryInVMAccessControlProfilesUpdateResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Update a gallery inVMAccessControlProfile.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param galleryName The name of the Shared Image Gallery.
   * @param inVMAccessControlProfileName The name of the gallery inVMAccessControlProfile to be
   *                                     retrieved.
   * @param galleryInVMAccessControlProfile Parameters supplied to the update gallery
   *                                        inVMAccessControlProfile operation.
   * @param options The options parameters.
   */
  async beginUpdateAndWait(
    resourceGroupName: string,
    galleryName: string,
    inVMAccessControlProfileName: string,
    galleryInVMAccessControlProfile: GalleryInVMAccessControlProfileUpdate,
    options?: GalleryInVMAccessControlProfilesUpdateOptionalParams,
  ): Promise<GalleryInVMAccessControlProfilesUpdateResponse> {
    const poller = await this.beginUpdate(
      resourceGroupName,
      galleryName,
      inVMAccessControlProfileName,
      galleryInVMAccessControlProfile,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * Delete a gallery inVMAccessControlProfile.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param galleryName The name of the Shared Image Gallery.
   * @param inVMAccessControlProfileName The name of the gallery inVMAccessControlProfile to be
   *                                     retrieved.
   * @param options The options parameters.
   */
  async beginDelete(
    resourceGroupName: string,
    galleryName: string,
    inVMAccessControlProfileName: string,
    options?: GalleryInVMAccessControlProfilesDeleteOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<GalleryInVMAccessControlProfilesDeleteResponse>,
      GalleryInVMAccessControlProfilesDeleteResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<GalleryInVMAccessControlProfilesDeleteResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: {
        resourceGroupName,
        galleryName,
        inVMAccessControlProfileName,
        options,
      },
      spec: deleteOperationSpec,
    });
    const poller = await createHttpPoller<
      GalleryInVMAccessControlProfilesDeleteResponse,
      OperationState<GalleryInVMAccessControlProfilesDeleteResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Delete a gallery inVMAccessControlProfile.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param galleryName The name of the Shared Image Gallery.
   * @param inVMAccessControlProfileName The name of the gallery inVMAccessControlProfile to be
   *                                     retrieved.
   * @param options The options parameters.
   */
  async beginDeleteAndWait(
    resourceGroupName: string,
    galleryName: string,
    inVMAccessControlProfileName: string,
    options?: GalleryInVMAccessControlProfilesDeleteOptionalParams,
  ): Promise<GalleryInVMAccessControlProfilesDeleteResponse> {
    const poller = await this.beginDelete(
      resourceGroupName,
      galleryName,
      inVMAccessControlProfileName,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * ListByGalleryNext
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param galleryName The name of the Shared Image Gallery.
   * @param nextLink The nextLink from the previous successful call to the ListByGallery method.
   * @param options The options parameters.
   */
  private _listByGalleryNext(
    resourceGroupName: string,
    galleryName: string,
    nextLink: string,
    options?: GalleryInVMAccessControlProfilesListByGalleryNextOptionalParams,
  ): Promise<GalleryInVMAccessControlProfilesListByGalleryNextResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, galleryName, nextLink, options },
      listByGalleryNextOperationSpec,
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const listByGalleryOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Compute/galleries/{galleryName}/inVMAccessControlProfiles",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.GalleryInVMAccessControlProfileList,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion3],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.galleryName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Compute/galleries/{galleryName}/inVMAccessControlProfiles/{inVMAccessControlProfileName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.GalleryInVMAccessControlProfile,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion3],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.galleryName,
    Parameters.inVMAccessControlProfileName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const createOrUpdateOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Compute/galleries/{galleryName}/inVMAccessControlProfiles/{inVMAccessControlProfileName}",
  httpMethod: "PUT",
  responses: {
    200: {
      bodyMapper: Mappers.GalleryInVMAccessControlProfile,
    },
    201: {
      bodyMapper: Mappers.GalleryInVMAccessControlProfile,
    },
    202: {
      bodyMapper: Mappers.GalleryInVMAccessControlProfile,
    },
    204: {
      bodyMapper: Mappers.GalleryInVMAccessControlProfile,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  requestBody: Parameters.galleryInVMAccessControlProfile,
  queryParameters: [Parameters.apiVersion3],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.galleryName,
    Parameters.inVMAccessControlProfileName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const updateOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Compute/galleries/{galleryName}/inVMAccessControlProfiles/{inVMAccessControlProfileName}",
  httpMethod: "PATCH",
  responses: {
    200: {
      bodyMapper: Mappers.GalleryInVMAccessControlProfile,
      headersMapper: Mappers.GalleryInVMAccessControlProfilesUpdateHeaders,
    },
    201: {
      bodyMapper: Mappers.GalleryInVMAccessControlProfile,
      headersMapper: Mappers.GalleryInVMAccessControlProfilesUpdateHeaders,
    },
    202: {
      bodyMapper: Mappers.GalleryInVMAccessControlProfile,
      headersMapper: Mappers.GalleryInVMAccessControlProfilesUpdateHeaders,
    },
    204: {
      bodyMapper: Mappers.GalleryInVMAccessControlProfile,
      headersMapper: Mappers.GalleryInVMAccessControlProfilesUpdateHeaders,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  requestBody: Parameters.galleryInVMAccessControlProfile1,
  queryParameters: [Parameters.apiVersion3],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.galleryName,
    Parameters.inVMAccessControlProfileName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const deleteOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Compute/galleries/{galleryName}/inVMAccessControlProfiles/{inVMAccessControlProfileName}",
  httpMethod: "DELETE",
  responses: {
    200: {
      headersMapper: Mappers.GalleryInVMAccessControlProfilesDeleteHeaders,
    },
    201: {
      headersMapper: Mappers.GalleryInVMAccessControlProfilesDeleteHeaders,
    },
    202: {
      headersMapper: Mappers.GalleryInVMAccessControlProfilesDeleteHeaders,
    },
    204: {
      headersMapper: Mappers.GalleryInVMAccessControlProfilesDeleteHeaders,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion3],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.galleryName,
    Parameters.inVMAccessControlProfileName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listByGalleryNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.GalleryInVMAccessControlProfileList,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  urlParameters: [
    Parameters.$host,
    Parameters.nextLink,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.galleryName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
